import { useEffect, useState } from "react";
import { Breadcrumb, BreadcrumbItem, Button, Col, Container, Form, Modal, Nav, Row, Table } from "react-bootstrap";
import { Typeahead } from "react-bootstrap-typeahead";
import "react-bootstrap-typeahead/css/Typeahead.css";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import { useGetSalarySlipForEmployeeMutation } from "../../slices/adminApiSlice";
import { useGetEmployeeListMutation, useGetOptionsMutation } from "../../slices/commonApiSlice";
import { parseOptionsData } from "../../utils/parseOptionsData";
import { parseUserInfo } from "../../utils/parseUtil";

const SalarySlipScreen = () => {
  const [month, setMonth] = useState("");
  const [year, setYear] = useState("");
  const [empType, setEmpType] = useState("");
  const [empName, setEmpName] = useState("");
  const [department, setDepartment] = useState("");
  const [designation, setDesignation] = useState("");
  const [empCode, setEmpCode] = useState("");
  const [activeType, setActiveType] = useState("0");
  const [language, setLanguage] = useState("0");
  const [showModal, setShowModal] = useState(false);
  const [pdfUrl, setPdfUrl] = useState("");
  const [employeeSuggestions, setEmployeeSuggestions] = useState([]);
  const [selectedEmpCode, setSelectedEmpCode] = useState("");
  const [submittedData, setSubmittedData] = useState(null);
  const [yearsOptions, setYearsOptions] = useState([]);

  const { userInfo } = useSelector((state) => state.auth);

  const [getSalarySlip, { isLoading }] = useGetSalarySlipForEmployeeMutation();
  const [getOptions, { isLoading: getOptionsLoading }] = useGetOptionsMutation();
  const [getEmpName, { isLoading: getEmpNameLoading }] = useGetEmployeeListMutation();

  const [employeeTypes, setEmployeeTypes] = useState([]);
  const [designations, setDesignations] = useState([]);
  const [departments, setDepartments] = useState([]);

  useEffect(() => {
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth() + 1;
    const previousMonth = currentMonth === 1 ? 12 : currentMonth - 1;

    const startYear = parseInt(process.env.REACT_APP_START_YEAR, 10 || 2020);

    const options = [];
    for (let i = startYear; i <= currentYear + 1; i++) {
      options.push(i);
    }

    setYearsOptions(options);
    setYear(currentYear.toString());

    setMonth(previousMonth.toString());

    // Fetch options data on component load
    const fetchOptions = async () => {
      try {
        const reqCompanyCode = userInfo[13]?.InfoField || "1"; // Default to '1' if not available

        const formData = new FormData();
        formData.append("title", "GetEmployeeMasterDataList");
        formData.append("ReqCompanyCode", reqCompanyCode);

        const res = await getOptions(formData);

        const parsedOptions = parseUserInfo(res);
        const parsedData = parseOptionsData(parsedOptions);

        console.log(parsedData, "12");

        if (parsedData) {
          setEmployeeTypes(parsedData.employeeTypes);
          setDesignations(parsedData.designations);
          setDepartments(parsedData.departments);
        } else {
          toast.error("Failed to load options");
        }
      } catch (err) {
        console.error("Error fetching options:", err);
        toast.error("Error fetching options");
      }
    };

    fetchOptions();
  }, [getOptions, userInfo]);

  const handleEmployeeSearch = async (input) => {
    if (input.length < 3) {
      setEmployeeSuggestions([]); // Clear suggestions if input is less than 3 characters
      return;
    }

    try {
      const reqCompanyCode = userInfo[13]?.InfoField || "1"; // Default to '1' if not available
      const formData = new FormData();
      formData.append("title", "GetEmployeeList");
      formData.append("ReqCompanyCode", reqCompanyCode);
      formData.append("ReqSearchStr", input);

      const res = await getEmpName(formData);
      const parsedData = parseUserInfo(res);

      if (parsedData && parsedData.length > 0) {
        const employeeList = JSON.parse(parsedData[0].JSONData1);
        setEmployeeSuggestions(employeeList);
      } else {
        setEmployeeSuggestions([]);
        toast.error("No employees found");
      }
    } catch (err) {
      console.error("Error fetching employee list:", err);
      toast.error("Error fetching employee list");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const reqCompanyCode = userInfo[13]?.InfoField;

    const formData = new FormData();
    formData.append("title", "GetSalarySlipForEmployee");
    formData.append("ReqYear", year);
    formData.append("ReqEmpCode", empCode);
    formData.append("ReqCompanyCode", reqCompanyCode);
    formData.append("ReqSlipType", language);
    formData.append("ReqMonth", month);
    formData.append("ReqActiveType", activeType);
    formData.append("ReqPayEmpType", empType);
    formData.append("ReqDepartment", department);
    formData.append("ReqDesig", designation);

    for (let [key, value] of formData.entries()) {
      console.log(`${key}: ${value}`);
    }

    setSubmittedData({
      empName,
      year,
      month,
      empType,
      empCode,
      activeType,
      language,
      department,
      designation,
      reqCompanyCode,
    });

    try {
      const res = await getSalarySlip(formData);

      const parsedData = parseUserInfo(res);

      if (parsedData && Array.isArray(parsedData) && parsedData.length > 0) {
        const data = parsedData[0];

        if (data.ErrorCode === "0") {
          let pdfPath = data.JSONData1;

          if (pdfPath.startsWith("/api/WebServiceForStaffData.aspx/")) {
            pdfPath = pdfPath.replace("/api/WebServiceForStaffData.aspx/", "/");
          }

          const backendUrl = process.env.REACT_APP_URL;
          const pdfUrl = `${backendUrl}${pdfPath}`;

          setPdfUrl(pdfUrl);
          setShowModal(true);
        } else {
          toast.error(data.ErrorMessage || "An error occurred");
        }
      } else {
        toast.error("Failed to parse user info");
      }
    } catch (err) {
      console.error("Error submitting data:", err);
      toast.error(err?.data?.message || "Error submitting data");
    }
  };

  return (
    <>
      <Breadcrumb>
        <BreadcrumbItem>
          <Nav.Link as={Link} to="/dashboard" className="text-success text-decoration-none">
            Dashboard
          </Nav.Link>
        </BreadcrumbItem>
        <BreadcrumbItem active>Print Salary Slip</BreadcrumbItem>
      </Breadcrumb>
      <Container className="form-container">
        <h4 className="text-center">Print Salary Slip</h4>
        <Form className="border p-4 rounded bg-light mb-4" onSubmit={handleSubmit}>
          <Row className="mb-3">
            <Col md={12}>
              <Form.Group controlId="empName">
                <Form.Label>Employee Name</Form.Label>
                <Typeahead
                  id="empName-typeahead"
                  onInputChange={(input) => {
                    setEmpName(input);
                    handleEmployeeSearch(input);
                  }}
                  onChange={(selected) => {
                    if (selected.length > 0) {
                      setEmpName(selected[0].EmployeeName);
                      setSelectedEmpCode(selected[0].Empcode); // Save the employee code
                      setEmpCode(selected[0].Empcode);
                    }
                  }}
                  options={employeeSuggestions}
                  labelKey="EmployeeName"
                  placeholder="Choose an employee..."
                  selected={empName ? [{ EmployeeName: empName }] : []}
                  className="form-control-hover"
                  minLength={3}
                />
              </Form.Group>
            </Col>
          </Row>
          <Row className="mb-3">
            <Col md={6}>
              <Form.Group controlId="month">
                <Form.Label>Month</Form.Label>
                <Form.Control as="select" value={month} onChange={(e) => setMonth(e.target.value)} className="form-control-hover">
                  <option value="1">January</option>
                  <option value="2">February</option>
                  <option value="3">March</option>
                  <option value="4">April</option>
                  <option value="5">May</option>
                  <option value="6">June</option>
                  <option value="7">July</option>
                  <option value="8">August</option>
                  <option value="9">September</option>
                  <option value="10">October</option>
                  <option value="11">November</option>
                  <option value="12">December</option>
                </Form.Control>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group controlId="year">
                <Form.Label>Year</Form.Label>
                <Form.Control as="select" value={year} onChange={(e) => setYear(e.target.value)} className="form-control-hover" required>
                  {yearsOptions.map((yr) => (
                    <option key={yr} value={yr}>
                      {yr}
                    </option>
                  ))}
                </Form.Control>
              </Form.Group>
            </Col>
          </Row>
          <Row>
            <Col md={6}>
              <Form.Group controlId="empCode" className="mb-3">
                <Form.Label>Employee Code</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Enter Employee Code"
                  value={empCode}
                  onChange={(e) => setEmpCode(e.target.value)}
                  className="form-control-hover"
                />
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group controlId="department">
                <Form.Label>Department</Form.Label>
                <Typeahead
                  id="department-typeahead"
                  onChange={(selected) => setDepartment(selected[0])}
                  options={departments}
                  placeholder="Choose a department..."
                  selected={department ? [department] : []}
                  className="form-control-hover"
                />
              </Form.Group>
            </Col>
          </Row>
          <Row className="mb-3">
            <Col md={6}>
              <Form.Group controlId="designation">
                <Form.Label>Designation</Form.Label>
                <Typeahead
                  id="designation-typeahead"
                  onChange={(selected) => setDesignation(selected[0])}
                  options={designations}
                  placeholder="Choose a designation..."
                  selected={designation ? [designation] : []}
                  className="form-control-hover"
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group controlId="empType">
                <Form.Label>Employee Type</Form.Label>
                <Typeahead
                  id="empType-typeahead"
                  onChange={(selected) => setEmpType(selected[0])}
                  options={employeeTypes}
                  placeholder="Choose an employee type..."
                  selected={empType ? [empType] : []}
                  className="form-control-hover"
                />
              </Form.Group>
            </Col>
          </Row>
          <Row className="mb-3">
            <Col md={6}>
              <Form.Group controlId="activeType">
                <Form.Label>Active Type</Form.Label>
                <Form.Control as="select" value={activeType} onChange={(e) => setActiveType(e.target.value)} className="form-control-hover">
                  <option value="0">All</option>
                  <option value="1">Active</option>
                  <option value="2">Dropped</option>
                </Form.Control>
              </Form.Group>
            </Col>
            <Col className="radio-box">
              <Form.Group>
                <Form.Label>
                  <div className="headline">Language</div>
                </Form.Label>
                <div className="d-flex">
                  <Form.Check
                    type="radio"
                    id="english"
                    name="language"
                    label="English"
                    value="0"
                    checked={language === "0"}
                    onChange={(e) => setLanguage(e.target.value)}
                    className="mx-3"
                  />
                  <Form.Check
                    type="radio"
                    id="hindi"
                    name="language"
                    label="Hindi"
                    value="1"
                    checked={language === "1"}
                    onChange={(e) => setLanguage(e.target.value)}
                  />
                </div>
              </Form.Group>
            </Col>
          </Row>
          <Row>
            <Col className="text-center">
              <Button type="submit" variant="primary" className="px-4" disabled={isLoading || getOptionsLoading}>
                {isLoading || getOptionsLoading ? "Loading..." : "Submit"}
              </Button>
            </Col>
          </Row>
        </Form>

        {/* Render table with submitted data */}
        {submittedData && (
          <Table striped bordered hover className="mt-4">
            <thead>
              <tr>
                <th>Employee Name</th>
                <th>Year</th>
                <th>Month</th>
                <th>Employee Type</th>
                <th>Employee Code</th>
                <th>Active Type</th>
                <th>Language</th>
                <th>Department</th>
                <th>Designation</th>
                <th>Company Code</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>{submittedData.empName}</td>
                <td>{submittedData.year}</td>
                <td>{submittedData.month}</td>
                <td>{submittedData.empType}</td>
                <td>{submittedData.empCode}</td>
                <td>{submittedData.activeType}</td>
                <td>{submittedData.language}</td>
                <td>{submittedData.department}</td>
                <td>{submittedData.designation}</td>
                <td>{submittedData.reqCompanyCode}</td>
              </tr>
            </tbody>
          </Table>
        )}

        <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>Salary Slip PDF</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <iframe src={pdfUrl} width="100%" height="500px" title="Salary Slip PDF"></iframe>
          </Modal.Body>
        </Modal>
      </Container>
    </>
  );
};

export default SalarySlipScreen;
